import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { memo, useEffect, useRef, useState } from 'react';
import type { LayoutProps } from '../../../../types/mfc';
import { ViewMode } from '../../../constants/enums';
import { IOS_PWA_STATUS_BAR_BLUR, isIOS, isTouchDevice } from '../../../utilities/browser';
import dynamicWithRetry, { loadWithRetry } from '../../../utilities/dynamicWithRetry';
import { isNavOnBottom } from '../../../utilities/layout';
import { AppStateType, useAppStateSelector } from '../../Hooks/useAppState';
import { hideDragHelper } from '../../Hooks/useDragHelper';
import { useHealthAppState } from '../../Hooks/useHealthAppState';
import { useLayoutSelector } from '../../Hooks/useLayout';
import { useFloatingRoomStateSelector } from '../../Hooks/useModal';
import { useModelsLoadedAppState } from '../../Hooks/useModelsLoadedAppState';
import { usePassive } from '../../Hooks/usePassive';
import { usePinnedRoomCount } from '../../Hooks/useRoom';
import { useRouteSelector } from '../../Hooks/useRoute';
import { useScreenStateSelector } from '../../Hooks/useScreenState';
import { useSidebarSelector } from '../../Hooks/useSidebar';
import RequiredChange from '../Authentication/RequiredChange';
import UnverifiedAccount from '../Authentication/UnverifiedAccount';
import Featured from '../Featured';
import LoadingScreen from '../LoadingScreen';
import Maintenance from '../Maintenance';
import Floaters from './Floaters';
import Header from './Header';
import Modals from './Modals';
import Nav from './Nav';
import "./index.linaria.module.css!=!./index?./index.linaria.module.css?css=H4sIAAAAAAAAA91UwZKjIBC971/sYWtmDmQkkzEJfgyFggRFIICisfbftzTRkEzNYav2tBfLft2vu3nV3RsHfU9hux8vQCjKerSFu/3u8JHu0p+iMdp6onxmtBNeaIVI7rRsPctOTPCTRzBJfmVBUH+6/uakqLnVraKg0FJb1BH7CsAdfst+/9g4yPuECzNS4YwkAwKB5bXwINd99oyVkkVg42YgDpwDvuknSgwMKWpUMOWZzZY8j+AtuGqdF+UACq08U35xfwPrjtlS6oBOglKmru+z0MGWjHH5qRxKsvhZgFsdJmztZta5YyjJYv9/KhPxDd463HBVh+2jNUaDZJlhxCOlb38rtcHwlOqqvTyZMXmd3FsbCzdgSEu7M9WTGXOduDBUTI0vtKbGzqtwOIxfN+IWwrYYanfW226Mxb55u2H1RvIvTXG8K0N9ujxacUuiIZyh1srXl3fniRfFuyGyAd4y5kDOgZxKbirDX96WtPUOQ0nyoPSoDSmEH1CygVFRuD/n/PPzyfybspvAcnOvWFHc1p/hMx0b0YNcMkVBoylDk5aSDFFn6tB3JxU1doy0huroU5vfxS5Fz+gS4GssqCn5bvTaoGSB1ccC21n+1ZGKxZFr73Vz95TnxSNZGTHCGYecHwX/eh33a5sV5nIIZTMaLaYRA6xjyjuktFqHYkvxh69S2T1a4zfncpK4dSAnFpRCylnXAuZDCMfLP7yZq6yWSTKdnWzaWSIUs0CRZpr9eYXBCi/vIRZ3titY/2iN862gwrLimleHhZEzDPkh4Wn6ZF45wRKDpk+0Rmqv6aFatqhVjq2rX2I4pE3C+aM1Eim4Wi/PnNl5YifeH6gx/wfqBgAA"

// just delay the loading instead of dynamic loading it, so when the user tries
// to open menu or go to a room the component is there already while still not
// being included in the main
loadWithRetry(() => import('../Chat/Room/PinnedRooms')).catch(() => undefined);
const PinContainer = dynamicWithRetry(() => import('../Chat/Room/PinnedRooms'));
loadWithRetry(() => import('../Sidebar')).catch(() => undefined);
const Sidebar = dynamicWithRetry(() => import('../Sidebar'));
const StyledLoadingScreenContainer = /*#__PURE__*/styled('div')({
  name: "StyledLoadingScreenContainer",
  class: "s1txd1u7",
  propsAsIs: false
});
const StyledDivRoot = /*#__PURE__*/styled('div')({
  name: "StyledDivRoot",
  class: "s1gx0gip",
  propsAsIs: false
});
const StyledChildrenContainer = /*#__PURE__*/styled('div')({
  name: "StyledChildrenContainer",
  class: "s1r1s1ua",
  propsAsIs: false
});
const stylesBackdrop = "atm_2s_mgnkw2 atm_2m_1h6ojuz atm_2w_1dfr4pj atm_mk_stnw88 atm_e2_1osqo2v atm_vy_1osqo2v";
const stylesBackdropLightMode = "atm_2g_4fwkhz atm_k4_1labwno";
const stylesBackdropDarkMode = "atm_2g_17qbg55 atm_jd_uk5w56 atm_k4_n8xvhn";

// iOS 27 PWA: an empty translucent fixed layer (same shape as the drawer Backdrop) makes WebKit
// fill the status bar with --background instead of blurring the header; pointer-events are ignored
const statusBarFillStyles = "atm_mk_1n9t6rb atm_tk_idpfg4 atm_n3_idpfg4 atm_6i_idpfg4 atm_fq_idpfg4 atm_wq_wbg9ig atm_mj_glywfm atm_2d_3tj6lv";
const ContentContainer = /*#__PURE__*/styled('div')({
  name: "ContentContainer",
  class: "c1byww9z",
  propsAsIs: false
});
const verticalStyles = "atm_ar_vrvcex atm_be_1g80g66 atm_e2_n7od8j atm_f_1y6m0gg";
const dropPin = () => {
  // this is so the pin helper can still close itself if a pin is dropped on it
  // without actually pinning anything, no matter where its dropped
  hideDragHelper();
};
const Layout = memo((props: LayoutProps) => {
  const {
    authenticated,
    darkMode,
    // children,
    username,
    isMobile,
    loading,
    level,
    uid,
    ssr
  } = props;
  const {
    connecting: socketConnecting,
    connected: socketConnected,
    paused: socketPaused,
    error: socketError
  } = useHealthAppState();
  const requiresAccountChange = useAppStateSelector(AppStateType.APP, 'requiresAccountChange');
  const unverified = useAppStateSelector(AppStateType.APP, 'unverified');
  const maintenanceMode = useAppStateSelector(AppStateType.APP, 'maintenanceMode');
  const modelsLoaded = useModelsLoadedAppState();
  const showSidebar = useSidebarSelector('show');
  const roomExpanded = useFloatingRoomStateSelector('expanded');
  const viewMode = useFloatingRoomStateSelector('viewMode');
  const fullWindow = viewMode === ViewMode.FullWindow;
  const leftNavExpanded = useLayoutSelector('expandedNav');
  const passiveMode = usePassive();
  const mobileOrTablet = isMobile || isIOS() || isTouchDevice;
  const [delayReconnectionScreen, setDelayReconnectionScreen] = useState(mobileOrTablet);
  const delayReconnectionScreenTimeout = useRef(null);
  const hasPinnedRooms = usePinnedRoomCount() > 0;
  const windowHeight = useScreenStateSelector('windowHeight');
  const portrait = useScreenStateSelector('portrait');
  const calculated = useScreenStateSelector('calculated');
  const windowWidth = useScreenStateSelector('windowWidth');
  const contentWidth = useScreenStateSelector('contentWidth');
  const contentHeight = useScreenStateSelector('contentHeight');
  const roomWidth = useScreenStateSelector('roomWidth');
  const roomHeight = useScreenStateSelector('roomHeight');
  const pinsWidth = useScreenStateSelector('pinsWidth');
  const singlePin = useScreenStateSelector('singlePin');
  const pinLimit = useScreenStateSelector('pinLimit');
  const [firstRender, setFirstRender] = useState(ssr);
  useEffect(() => setFirstRender(false), []);
  const shouldHidePins =
  // if we're in a full expanded room
  passiveMode && roomExpanded ||
  // or if we're in a room that's portrait (which should behave as expanded)
  passiveMode && portrait;
  const showBottomNav = isNavOnBottom(portrait, fullWindow);
  const showLeftNav = !portrait && !fullWindow;
  const showConnectingScreen = (socketConnecting || !socketConnected && !socketError) && modelsLoaded && !loading;
  const hideRoute = passiveMode && !isTouchDevice;
  const hideContent = requiresAccountChange || unverified;
  const usableWidth = portrait ? windowWidth : shouldHidePins ? roomWidth : contentWidth;
  const Page = useRouteSelector('baseComponent');
  useEffect(() => {
    if (!showConnectingScreen || !mobileOrTablet || socketPaused) {
      clearTimeout(delayReconnectionScreenTimeout.current);
      setDelayReconnectionScreen(mobileOrTablet);
      return;
    }
    setDelayReconnectionScreen(true);
    delayReconnectionScreenTimeout.current = setTimeout(() => {
      setDelayReconnectionScreen(false);
    }, 5000);
    return () => {
      clearTimeout(delayReconnectionScreenTimeout.current);
    };
  }, [showConnectingScreen, socketPaused]);
  const hideFeaturedModels = useAppStateSelector(AppStateType.APP, 'hideFeaturedModels');
  if (maintenanceMode || socketError) {
    return <Maintenance />;
  }
  if (firstRender) {
    // SSR shell: paint the splash from server HTML instead of a blank
    // page while hydration and the socket boot are still in flight.
    return <StyledLoadingScreenContainer>
                <LoadingScreen />
            </StyledLoadingScreenContainer>;
  }
  const isLoaded = !requiresAccountChange && !unverified && loading === false;
  const isMobileLandscape = windowHeight < 600 && !hideFeaturedModels;
  return <>
            {requiresAccountChange || unverified || loading && <StyledLoadingScreenContainer>
                        <LoadingScreen landscapeOffset={!hideFeaturedModels} />
                    </StyledLoadingScreenContainer>}
            <Modals delayReconnectionScreen={delayReconnectionScreen} showConnectingScreen={showConnectingScreen} />
            <div className={cx(stylesBackdrop, darkMode && stylesBackdropDarkMode, !darkMode && stylesBackdropLightMode)} />
            {IOS_PWA_STATUS_BAR_BLUR && <div aria-hidden data-testid="Layout__StatusBarFill" className={statusBarFillStyles} />}
            {!fullWindow && <Header authenticated={authenticated} darkMode={darkMode} isMobile={isMobile} level={level} showMenu={!portrait} username={username} uid={uid} vertical={portrait} />}
            <StyledDivRoot onDrop={dropPin}>
                {showLeftNav && <Nav authenticated={authenticated} isMobile={isMobile} vertical={portrait} />}
                {calculated && <StyledChildrenContainer className={portrait ? verticalStyles : ''}>
                        {portrait && <PinContainer width={pinsWidth} vertical={portrait} singlePin={singlePin} pinLimit={pinLimit} hidePins={shouldHidePins} />}
                        <ContentContainer style={{
          width: hasPinnedRooms ? usableWidth : windowWidth,
          height: passiveMode ? roomHeight : contentHeight,
          containerType: fullWindow ? 'normal' : 'inline-size'
        }}>
                            {unverified ? <UnverifiedAccount /> : requiresAccountChange ? <RequiredChange /> : hideRoute ? null : <Page ssr={ssr} />}
                        </ContentContainer>
                        {!portrait && <PinContainer width={pinsWidth} vertical={portrait} singlePin={singlePin} pinLimit={pinLimit} hidePins={shouldHidePins} />}
                    </StyledChildrenContainer>}
                {!hideContent && !fullWindow && showSidebar && <Sidebar />}
            </StyledDivRoot>
            {showBottomNav && <Nav authenticated={authenticated} showBottomNav={showBottomNav} isMobile={isMobile} vertical={portrait} />}
            {!hideContent && <Floaters isMobile={isMobile} />}
            {!hideFeaturedModels && !authenticated && <Featured isLoaded={isLoaded} isMobile={isMobile} showLeftNav={showLeftNav} showBottomNav={showBottomNav} leftNavExpanded={leftNavExpanded} isMobileLandscape={isMobileLandscape} />}
        </>;
});
export default Layout;